import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Eye, EyeOff, Pencil } from "lucide-react";
import { API, ARTICLE_CATEGORIES, COVER_PRESETS } from "@/lib/site";
import { CoverUpload } from "@/components/CoverUpload";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#060D17]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-all duration-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20";
const labelCls = "font-mono-x mb-2 block text-[10px] uppercase tracking-[0.22em] text-slate-400";

const INITIAL = { title: "", category: ARTICLE_CATEGORIES[0], excerpt: "", content: "", cover_image: "", published: true };

export const AdminArticles = () => {
  const [articles, setArticles] = useState([]);
  const [form, setForm] = useState(INITIAL);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const headers = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("hs_admin_token")}` } });

  const load = useCallback(() => {
    axios
      .get(`${API}/articles/all`, headers())
      .then(({ data }) => setArticles(data))
      .catch(() => toast.error("Could not load articles."));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/articles/${editingId}`, form, headers());
        toast.success("Article updated.");
      } else {
        await axios.post(`${API}/articles`, form, headers());
        toast.success("Article published.");
      }
      setForm(INITIAL);
      setEditingId(null);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(", ")
        : typeof detail === "string"
          ? detail
          : "Could not save article.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setForm({
      title: a.title,
      category: a.category,
      excerpt: a.excerpt,
      content: a.content,
      cover_image: a.cover_image || "",
      published: a.published,
    });
    document.querySelector('[data-testid="admin-articles-section"]')?.scrollIntoView({ behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(INITIAL);
  };

  const togglePublish = async (a) => {
    try {
      await axios.patch(
        `${API}/articles/${a.id}`,
        { title: a.title, category: a.category, excerpt: a.excerpt, content: a.content, published: !a.published },
        headers()
      );
      load();
    } catch {
      toast.error("Could not update article.");
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/articles/${a.id}`, headers());
      toast.success("Article deleted.");
      load();
    } catch {
      toast.error("Could not delete article.");
    }
  };

  return (
    <section data-testid="admin-articles-section" className="mt-14">
      <div className="border-t border-white/5 pt-10">
        <h2 className="font-display text-xl font-bold text-white">Knowledge Articles</h2>
        <p className="mt-1 text-sm text-slate-400">
          Publish healthcare awareness posts — they appear instantly on the Knowledge Hub page.
        </p>
      </div>

      <form onSubmit={save} className="mt-6 rounded-2xl border border-white/5 bg-[#0B192C]/60 p-7">
        {editingId && (
          <p data-testid="article-editing-banner" className="font-mono-x mb-5 text-[10px] uppercase tracking-[0.25em] text-amber-500">
            Editing existing article — save to update or cancel below
          </p>
        )}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label className={labelCls} htmlFor="a-title">Title *</label>
            <input id="a-title" data-testid="article-form-title" required minLength={4}
              placeholder="Article title" className={inputCls}
              value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          </div>
          <div>
            <label className={labelCls} htmlFor="a-category">Category *</label>
            <select id="a-category" data-testid="article-form-category" className={inputCls}
              value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
              {ARTICLE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="a-excerpt">Short Excerpt *</label>
            <input id="a-excerpt" data-testid="article-form-excerpt" required minLength={10}
              placeholder="One-line summary shown on the article card" className={inputCls}
              value={form.excerpt} onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Cover Image (optional)</label>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
              {COVER_PRESETS.map((p) => (
                <button
                  type="button"
                  key={p.label}
                  data-testid={`cover-preset-${p.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setForm((f) => ({ ...f, cover_image: p.url }))}
                  title={p.label}
                  className={`overflow-hidden rounded-lg border-2 transition-all duration-300 ${
                    form.cover_image === p.url ? "border-teal-400" : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={p.url} alt={p.label} loading="lazy" className="h-14 w-full object-cover" />
                </button>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <CoverUpload onUploaded={(url) => setForm((f) => ({ ...f, cover_image: url }))} />
              {form.cover_image && (
                <button
                  type="button"
                  data-testid="cover-clear-button"
                  onClick={() => setForm((f) => ({ ...f, cover_image: "" }))}
                  className="text-xs text-slate-500 underline transition-colors hover:text-red-400"
                >
                  Remove cover image
                </button>
              )}
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="a-content">Content * (separate paragraphs with a blank line)</label>
            <textarea id="a-content" data-testid="article-form-content" required minLength={20} rows={6}
              placeholder="Write the article here..." className={`${inputCls} resize-y`}
              value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} />
          </div>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            data-testid="article-form-submit"
            className="inline-flex items-center gap-2 rounded-full bg-teal-500 px-7 py-3.5 text-sm font-bold text-[#060D17] transition-all duration-300 hover:bg-teal-400 disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {saving ? "Saving..." : editingId ? "Save Changes" : "Publish Article"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={cancelEdit}
              data-testid="article-cancel-edit"
              className="rounded-full border border-white/10 px-6 py-3.5 text-xs font-semibold text-slate-300 transition-all duration-300 hover:border-red-500/40 hover:text-red-400"
            >
              Cancel Edit
            </button>
          )}
        </div>
      </form>

      <div className="mt-6 space-y-3">
        {articles.map((a) => (
          <div
            key={a.id}
            data-testid={`admin-article-row-${a.slug}`}
            className="flex flex-col gap-4 rounded-2xl border border-white/5 bg-[#0B192C]/40 p-5 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <p className="font-display truncate text-sm font-bold text-white">{a.title}</p>
              <p className="font-mono-x mt-1 text-[10px] uppercase tracking-[0.2em] text-teal-400">
                {a.category} ·{" "}
                <span className={a.published ? "text-emerald-400" : "text-slate-500"}>
                  {a.published ? "Published" : "Hidden"}
                </span>
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                onClick={() => startEdit(a)}
                data-testid={`article-edit-${a.slug}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-all hover:border-amber-500/40 hover:text-amber-400"
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </button>
              <button
                onClick={() => togglePublish(a)}
                data-testid={`article-toggle-${a.slug}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-all hover:border-teal-500/40 hover:text-teal-300"
              >
                {a.published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {a.published ? "Hide" : "Publish"}
              </button>
              <button
                onClick={() => remove(a)}
                data-testid={`article-delete-${a.slug}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-all hover:border-red-500/40 hover:text-red-400"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
